import { useMemo, useState } from 'react';
import { Avatar, Button, Card, EmptyState, IconTile, ListRow, Money, SectionLabel, Stack } from '../../components';
import { CheckIcon, ChevronDownIcon, SwapIcon } from '../../components/icons';
import { minimumTransfers } from '../../lib/settle';
import type { MemberBalance } from '../../lib/settle';
import { buildOwesMatrix, pairwiseFor } from '../../lib/pairwise';
import type { PairwisePosition } from '../../lib/pairwise';
import { formatDate } from '../../lib/dates';
import { formatMoney } from '../../lib/money';
import { recordSettlement, undoSettlement } from '../../data/expenses';
import { useSubmit } from '../../hooks/useSubmit';
import { useTrip } from './TripContext';

export function SettleTab() {
  const { trip, balances, expenses, settlements, myMemberId, nameOf, reload } = useTrip();
  const submit = useSubmit();
  const [openMemberId, setOpenMemberId] = useState<string | null>(null);

  // Gross flows behind the net figures. Derived from data already loaded, so
  // opening a breakdown costs nothing but a render.
  const owes = useMemo(() => buildOwesMatrix(expenses, settlements), [expenses, settlements]);

  const memberBalances = useMemo<MemberBalance[]>(
    () =>
      balances.map((b) => ({
        memberId: b.member_id,
        displayName: b.display_name,
        netBase: Number(b.net_base),
      })),
    [balances],
  );

  const transfers = useMemo(() => minimumTransfers(memberBalances), [memberBalances]);
  const largest = Math.max(0.01, ...memberBalances.map((b) => Math.abs(b.netBase)));

  async function markPaid(fromMemberId: string, toMemberId: string, baseAmount: number) {
    const ok = await submit.run(() =>
      recordSettlement({ tripId: trip.id, fromMemberId, toMemberId, baseAmount }),
    );
    if (ok) reload();
  }

  async function undo(settlementId: string) {
    if (await submit.run(() => undoSettlement(settlementId))) reload();
  }

  return (
    <Stack gap={5}>
      <h1 style={{ fontSize: 'var(--fs-display)', fontWeight: 300, letterSpacing: '-0.02em' }}>
        Settle up
      </h1>

      {submit.error && (
        <div style={{ color: 'var(--error)', fontSize: 'var(--fs-body-sm)' }}>{submit.error}</div>
      )}

      <div>
        <SectionLabel>Where everyone stands</SectionLabel>
        <Card flush>
          {memberBalances.map((member, index) => {
            const isYou = member.memberId === myMemberId;
            const isOpen = openMemberId === member.memberId;
            const breakdown = isOpen
              ? pairwiseFor(member.memberId, memberBalances.map((m) => m.memberId), owes)
              : [];

            return (
              <div
                key={member.memberId}
                style={{
                  borderTop: index > 0 ? '1px solid var(--border)' : 'none',
                  background: isYou ? 'var(--row-highlight)' : undefined,
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenMemberId(isOpen ? null : member.memberId)}
                  aria-expanded={isOpen}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: 'var(--sp-4) var(--sp-5)',
                    background: 'none',
                    border: 'none',
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', fontSize: 'var(--fs-h3)', minWidth: 0 }}>
                      <Avatar memberId={member.memberId} displayName={member.displayName} size={34} />
                      {member.displayName}
                      {isYou && (
                        <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-sm)', fontWeight: 600 }}>
                          · you
                        </span>
                      )}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                      <Money
                        amount={member.netBase}
                        currency={trip.base_currency}
                        size="var(--fs-h2)"
                        tone="auto"
                        signed
                      />
                      <ChevronDownIcon
                        size={16}
                        style={{
                          color: 'var(--text-muted)',
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 160ms ease',
                        }}
                      />
                    </span>
                  </div>
                  <div
                    style={{
                      marginTop: 'var(--sp-3)',
                      height: 6,
                      background: 'var(--track)',
                      borderRadius: 'var(--r-pill)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, (Math.abs(member.netBase) / largest) * 100)}%`,
                        height: '100%',
                        borderRadius: 'var(--r-pill)',
                        background: member.netBase < -0.005 ? 'var(--error)' : 'var(--accent)',
                        transition: 'width 400ms ease',
                      }}
                    />
                  </div>
                </button>

                {isOpen && (
                  <Breakdown
                    subject={member}
                    positions={breakdown}
                    baseCurrency={trip.base_currency}
                    nameOf={nameOf}
                  />
                )}
              </div>
            );
          })}
        </Card>
      </div>

      <div>
        <SectionLabel>Suggested payments</SectionLabel>
        {transfers.length === 0 ? (
          <Card>
            <EmptyState title="Everyone is square">
              Nothing to pay. Balances update as soon as anyone adds an expense.
            </EmptyState>
          </Card>
        ) : (
          <Card flush>
            {transfers.map((transfer, index) => {
              const involvesMe =
                transfer.fromMemberId === myMemberId || transfer.toMemberId === myMemberId;
              return (
                <ListRow key={`${transfer.fromMemberId}-${transfer.toMemberId}`} divider={index > 0}>
                  <IconTile size={38} background={involvesMe ? 'var(--accent-soft)' : 'var(--accent-subtle)'}>
                    <SwapIcon size={18} />
                  </IconTile>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--fs-body-lg)' }}>
                      {transfer.fromMemberId === myMemberId ? 'You' : nameOf(transfer.fromMemberId)} →{' '}
                      {transfer.toMemberId === myMemberId ? 'you' : nameOf(transfer.toMemberId)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
                      {formatMoney(transfer.baseAmount, trip.base_currency)} {trip.base_currency}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={involvesMe ? 'primary' : 'secondary'}
                    disabled={submit.submitting}
                    onClick={() =>
                      void markPaid(transfer.fromMemberId, transfer.toMemberId, transfer.baseAmount)
                    }
                  >
                    <CheckIcon size={15} />
                    Paid
                  </Button>
                </ListRow>
              );
            })}
          </Card>
        )}
        <p style={{ marginTop: 'var(--sp-3)', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
          The fewest payments that clear everyone. Marking one as paid records it — it does not move
          any money.
        </p>
      </div>

      {settlements.length > 0 && (
        <div>
          <SectionLabel>Already paid</SectionLabel>
          <Card flush>
            {settlements.map((settlement, index) => (
              <ListRow key={settlement.id} divider={index > 0}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--fs-body-lg)' }}>
                    {nameOf(settlement.from_member_id)} → {nameOf(settlement.to_member_id)}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
                    {formatDate(settlement.settled_at)}
                  </div>
                </div>
                <Money amount={settlement.base_amount} currency={trip.base_currency} />
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={submit.submitting}
                  onClick={() => void undo(settlement.id)}
                >
                  Undo
                </Button>
              </ListRow>
            ))}
          </Card>
        </div>
      )}
    </Stack>
  );
}

/**
 * What the net figure above is made of: every person this one has lent to or
 * borrowed from, gross both ways.
 *
 * This exists because the netting is genuinely counter-intuitive. "Abdallah
 * paid for the hotels and we split them three ways, so I owe him" is a correct
 * observation with a wrong conclusion, and no amount of staring at a single
 * net number resolves it. The two gross figures do.
 */
function Breakdown({
  subject,
  positions,
  baseCurrency,
  nameOf,
}: {
  subject: MemberBalance;
  positions: PairwisePosition[];
  baseCurrency: string;
  nameOf: (memberId: string) => string;
}) {
  const first = subject.displayName;

  if (positions.length === 0) {
    return (
      <div style={{ padding: '0 var(--sp-5) var(--sp-4)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
        Square with everyone.
      </div>
    );
  }

  return (
    <div style={{ padding: '0 var(--sp-5) var(--sp-4)' }}>
      <div
        style={{
          borderTop: '1px solid var(--border)',
          paddingTop: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        {positions.map((position) => {
          const other = nameOf(position.otherId);
          const owedToSubject = position.net > 0;

          return (
            <div key={position.otherId}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                <span style={{ fontSize: 'var(--fs-body-lg)' }}>{other}</span>
                <span style={{ fontSize: 'var(--fs-body-sm)', color: owedToSubject ? 'var(--accent)' : 'var(--error)' }}>
                  {owedToSubject ? `owes ${first}` : `${first} owes them`}{' '}
                  <Money amount={Math.abs(position.net)} currency={baseCurrency} weight={600} />
                </span>
              </div>

              {/* The workings. Both directions, because one of them is always
                  the number someone is about to argue with. */}
              <div
                style={{
                  marginTop: 2,
                  fontSize: 'var(--fs-caption)',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  gap: 'var(--sp-4)',
                }}
              >
                <span>
                  {first} paid for them <Money amount={position.theyOwe} currency={baseCurrency} />
                </span>
                <span>
                  they paid for {first} <Money amount={position.youOwe} currency={baseCurrency} />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
